import React, { useEffect, useMemo, useState } from "react";
import PremiumModal from "./PremiumModal";
import LifetimePremiumModal from "./LifetimePremiumModal";
import ConfettiCanvas from "./ConfettiCanvas";
import { useUser } from "../hooks/useUser";

// Забаненным пользователям не показываем дешёвые краткосрочные планы
const HIDDEN_PLANS_FOR_BANNED = new Set(["month"]);

// Контейнер: выбирает popup по режиму оплаты из админки.
// subscription_enabled=true  → планы подписки (PremiumModal)
// subscription_enabled=false → lifetime one-time (LifetimePremiumModal)
function PremiumModalContainer({ isOpen, onClose }) {
  const { user, hasAccess, isBanned } = useUser();
  const [plans, setPlans] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // "processing" | "success" | "error"
  // null = ещё не загрузили; по умолчанию считаем subscription после загрузки
  const [subscriptionEnabled, setSubscriptionEnabled] = useState(null);

  // Если доступ уже активен — закрываем модалку
  useEffect(() => {
    if (isOpen && hasAccess()) {
      onClose();
    }
  }, [isOpen, user, hasAccess, onClose]);

  // Режим оплаты + планы (цены с сервера из .env)
  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    const fetchModeAndPlans = async () => {
      try {
        const modeRes = await fetch("/api/payment/mode");
        let enabled = true;
        if (modeRes.ok) {
          const modeData = await modeRes.json();
          if (typeof modeData.subscriptionEnabled === "boolean") {
            enabled = modeData.subscriptionEnabled;
          }
        }
        if (!cancelled) {
          setSubscriptionEnabled(enabled);
        }

        // Планы нужны только в subscription-режиме
        if (!enabled) return;

        const res = await fetch("/api/payment/plans");
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && Array.isArray(data.plans) && data.plans.length) {
          setPlans(data.plans);
        }
      } catch (error) {
        console.error("Error fetching payment mode/plans:", error);
        if (!cancelled) {
          // При ошибке не ломаем UI — оставляем subscription как безопасный дефолт
          setSubscriptionEnabled(true);
        }
      }
    };

    fetchModeAndPlans();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // Сбрасываем статус при открытии
  useEffect(() => {
    if (isOpen) {
      setPaymentStatus(null);
      setIsProcessing(false);
    }
  }, [isOpen]);

  const handleSelectPlan = async (plan) => {
    if (!plan) return;

    if (!window.Telegram?.WebApp) {
      setPaymentStatus("error");
      alert("Оплата доступна только в Telegram");
      return;
    }

    if (!user?.telegram_id) {
      setPaymentStatus("error");
      return;
    }

    setIsProcessing(true);
    setPaymentStatus("processing");

    try {
      const res = await fetch("/api/payment/create-stars-invoice", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          telegram_id: user.telegram_id,
          plan_id: plan.id,
          amount: plan.price,
        }),
      });

      if (!res.ok) {
        throw new Error("Invoice failed");
      }

      const { invoice_link } = await res.json();

      window.Telegram.WebApp.openInvoice(invoice_link, (status) => {
        if (status === "paid") {
          setPaymentStatus("success");
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        } else if (status === "cancelled") {
          setPaymentStatus(null);
          setIsProcessing(false);
        } else {
          setPaymentStatus("error");
          setIsProcessing(false);
        }
      });
    } catch (error) {
      console.error("Stars payment error:", error);
      setPaymentStatus("error");
      setIsProcessing(false);
    }
  };

  // Для забаненных скрываем дешёвые планы (например "month")
  const visiblePlans = useMemo(() => {
    if (!plans) return null;
    if (isBanned()) {
      return plans.filter((plan) => !HIDDEN_PLANS_FOR_BANNED.has(plan.id));
    }
    return plans;
  }, [plans, isBanned]);

  if (!isOpen) return null;

  // Пока режим не загружен — ничего не показываем (избегаем мигания неверного popup)
  if (subscriptionEnabled === null) return null;

  // Lifetime mode — старый popup
  if (!subscriptionEnabled) {
    return <LifetimePremiumModal isOpen={isOpen} onClose={onClose} />;
  }

  const statusMessage =
    paymentStatus === "success"
      ? "✅ Payment successful! Updating access..."
      : paymentStatus === "error"
        ? "❌ Payment error. Please try again."
        : paymentStatus === "processing"
          ? "⏳ Processing payment..."
          : null;

  const statusTone =
    paymentStatus === "success"
      ? "success"
      : paymentStatus === "error"
        ? "error"
        : null;

  return (
    <>
      <PremiumModal
        isOpen={isOpen}
        onClose={onClose}
        onSelectPlan={handleSelectPlan}
        plans={visiblePlans || undefined}
        isProcessing={isProcessing}
        statusMessage={statusMessage}
        statusTone={statusTone}
      />
      {paymentStatus === "success" && (
        <ConfettiCanvas anchorSelector=".pmx-window" />
      )}
    </>
  );
}

export default PremiumModalContainer;
